import React, { useState, useEffect } from 'react';
import { Check, Search, Users, Plus, Edit2 } from 'lucide-react';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';

const SkillDrawer = ({ 
  isOpen, 
  onClose, 
  skill, 
  employees, 
  onAssign, 
  onRename,
  getEmployeeColor 
}) => {
  const [search, setSearch] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [localSkillName, setLocalSkillName] = useState('');

  useEffect(() => {
    if (isOpen && skill && employees) {
      const cleanSkill = skill.startsWith('skill_') ? skill.replace(/^skill_/i, '') : skill;
      setLocalSkillName(cleanSkill);
      
      const fullSkillId = skill.startsWith('skill_') ? skill : `skill_${skill}`;
      const currentlyAssigned = employees
        .filter(emp => (emp.skills || []).includes(fullSkillId))
        .map(emp => emp.id);
      setSelectedEmps(currentlyAssigned);
      setSearch('');
    }
  }, [isOpen, skill, employees]);

  const handleSave = async () => {
    setIsProcessing(true);
    
    // 1. Handle Rename if changed
    const originalCleanName = skill.startsWith('skill_') ? skill.replace(/^skill_/i, '') : skill;
    if (localSkillName.trim() && localSkillName !== originalCleanName) {
      await onRename(skill, localSkillName);
    }

    // 2. Handle Assignments
    // Note: If renamed, the skill ID in the backend might have changed. 
    // However, our current onRename in SkillsTab reloads the data.
    // To be safe, we should assign to the *new* name if renamed.
    const targetSkill = localSkillName.trim() !== originalCleanName ? localSkillName : skill;
    await onAssign(targetSkill, selectedEmps);
    
    setIsProcessing(false);
    onClose();
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    const aSel = selectedEmps.includes(a.id);
    const bSel = selectedEmps.includes(b.id);
    if (aSel && !bSel) return -1;
    if (!aSel && bSel) return 1;
    return a.name.localeCompare(b.name);
  });

  const drawerTitle = (
    <div className="relative group flex-1">
      <Edit2 className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      <input 
        type="text"
        value={localSkillName}
        onChange={(e) => setLocalSkillName(e.target.value)}
        className="w-full bg-transparent border-none outline-none text-xl font-bold text-slate-900 tracking-tight p-0 focus:ring-0"
        style={{ fontFamily: 'var(--font-header)' }}
        placeholder="Skill Name"
      />
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="secondary" onClick={onClose} className="px-6 h-[36px] text-xs font-bold">Cancel</Button>
      <Button 
        variant="primary" 
        onClick={handleSave} 
        isLoading={isProcessing} 
        className="px-8 h-[36px] text-xs font-bold"
        icon={Check}
      >
        Save Changes
      </Button>
    </div>
  );

  return (
    <Drawer 
      isOpen={isOpen} 
      onClose={onClose} 
      title={drawerTitle}
      footer={footer}
    >
      <div className="space-y-6 h-full flex flex-col">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-slate-400" />
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Skill Assignments</label>
            </div>
            <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{selectedEmps.length} Qualified</span>
          </div>

          <div className="relative group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-[var(--primary)] transition-colors" />
            <input 
              type="text" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              placeholder="Search staff members..." 
              className="w-full h-[44px] pl-11 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 outline-none transition-all" 
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
          <div className="grid grid-cols-1 gap-2">
            {filteredEmployees.map(emp => {
              const isSelected = selectedEmps.includes(emp.id);
              return (
                <button 
                  key={emp.id || emp.name} 
                  onClick={() => setSelectedEmps(prev => isSelected ? prev.filter(id => id !== emp.id) : [...prev, emp.id])}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center justify-between group
                    ${isSelected ? 'bg-[#042f2e] text-white border-transparent shadow-md' : 'bg-white border-slate-100 text-slate-600 hover:border-slate-300'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getEmployeeColor(emp.name) }} />
                    <span className="text-xs font-bold">{emp.name}</span>
                  </div>
                  {isSelected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 text-slate-300" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SkillDrawer;
